import React, { createContext, useEffect, useState } from 'react';
import { VistaprintAuth } from '@vp/auth-react';
import { Spinner } from '@vp/swan';
import { defineMessages } from '@vistaprint-org/digital-i18n-utils';
import { useTranslation } from 'react-i18next';
import { fetchUserByConnectedProduct } from '../../api';
import UserModel from '../../models/UserModel';
import SiteModel from '../../models/SiteModel';
import { Work } from '../../models/WorkModel';
import ErrorPage from '../pages/ErrorPage';
import vpAccessTokenKeys from '../../../common/vpAccessTokenKeys';

const messages = defineMessages({
  loading: 'Loading',
});

// Guards against a redirect loop: the vistaprint.com edge worker can downgrade
// a signed-in shopper's session to a fresh anonymous one (missing/unparseable
// vpauth cookie), which 403s here even though the user was signed in a moment
// ago. Bounce to sign-in once per altId per tab; if that doesn't fix it, fall
// through to the normal error page instead of looping forever.
const getSignInBounceKey = (altId: string): string =>
  `csb-connected-product-403-sign-in-bounce-${altId}`;
interface SessionContextModel {
  userData: UserModel;
  siteData: SiteModel;
  tenant?: string; // TODO VBC-828 remove
  work?: Work | null; // TODO VBC-828 remove
  setSiteData: (siteData: SiteModel) => void;
}

const SessionContext = createContext<SessionContextModel>({} as SessionContextModel);

const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState<UserModel | null>(null);
  const [siteData, setSiteData] = useState<SiteModel | null>(null);
  const [statusError, setStatusError] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const auth = new (VistaprintAuth.WebAuth as any)();
  const identity = auth.getIdentity();
  const loggedInAsCareAgent = identity.profile[vpAccessTokenKeys.isInternal];

  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(document.location.search.substring(1));
      const connectedProductAltId = params.get('altId');
      const workId = params.get('workId');

      if (!connectedProductAltId && !workId) {
        alert(`Either AltId or WorkId should be specified`);
      }

      let user;
      try {
        user = await fetchUserByConnectedProduct({
          connectedProductAltId: connectedProductAltId,
          workId: workId,
        });
      } catch (e) {
        if (e.response?.status === 403 && connectedProductAltId && !identity.isSignedIn) {
          const bounceKey = getSignInBounceKey(connectedProductAltId);
          if (!sessionStorage.getItem(bounceKey)) {
            sessionStorage.setItem(bounceKey, '1');
            try {
              auth.signIn();
              return;
            } catch (signInError) {
              setStatusError(403);
              return;
            }
          }
        }
        setStatusError(e.response?.status === 403 ? 403 : 404);
        return;
      }

      // TODO VBC-680 better error msgs for these error cases
      if (!user) {
        setStatusError(403);
        return;
      }
      if (!user && loggedInAsCareAgent) {
        setStatusError(409);
        return;
      }
      if (connectedProductAltId) {
        sessionStorage.removeItem(getSignInBounceKey(connectedProductAltId));
      }
      setUserData(user);
      setSiteData(user.connectedProduct.siteModel);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (statusError) {
    return <ErrorPage errorCode={statusError} />;
  }

  return siteData && userData ? (
    <SessionContext.Provider value={{ userData, siteData, setSiteData }}>
      {children}
    </SessionContext.Provider>
  ) : (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Spinner accessibleText={t(messages.loading.id)} size="super" />
    </div>
  );
};

export { SessionContext, SessionProvider };
